import type {
  CellData,
  GridId,
  GridLayout,
  GridMap,
  GridPosition,
  GridResult,
  PinData,
  PositionData,
  ScreenLayout,
  ScreenMethods,
} from "@observe-tech/utils";
import type { SyncStore as Store } from "../../infra/state";

export const createGridOperations = (
  store: Store,
  findScreenLayout: (screenId: number) => ScreenLayout,
  setScreenLayout: (screenId: number, layout: ScreenLayout) => ScreenLayout,
  shouldUseCameraGrid: (layout: ScreenLayout) => boolean,
  gridToUse: (layout: ScreenLayout) => GridMap,
  positionToGridId: (position: GridPosition) => GridId,
  isValidGridLayout: (gridLayout: GridLayout) => boolean,
  validateCagePositionData: (
    data: PositionData,
  ) => CellData<[number, ...number[]]>,
  validateCameraPositionData: (data: PositionData) => CellData<[number]>,
  adjustFocusedPositionsForNewLayout: (
    layout: ScreenLayout,
    gridLayout: GridLayout,
  ) => ScreenLayout,
  findExistingCameraPosition: (
    store: Store,
    targetData: CellData,
    excludeGridId?: GridId,
  ) => { screenId: number; gridId: GridId; data: CellData } | null,
  findExistingPinPosition: (
    store: Store,
    feedlineId: string,
    machineId: number,
    excludeScreenId?: number,
    excludeGridId?: GridId,
  ) => { screenId: number; gridId: GridId } | null,
) => {
  const getGridData: ScreenMethods["getGridData"] = async (screenId) => {
    const layout = findScreenLayout(screenId);

    let gridData: GridResult["gridData"];
    if (shouldUseCameraGrid(layout)) {
      gridData = layout.screenData.cameraGrid;
    } else {
      // limit number of cameras to nCams
      const nCams = layout.screenData.nCams;

      gridData = Object.fromEntries(
        Object.entries(layout.screenData.cageGrid).map(([key, value]) => {
          if (value && value.cameras.length > nCams) {
            return [
              key,
              {
                ...value,
                cameras: value.cameras.slice(0, nCams) as [number, ...number[]],
              },
            ];
          }
          return [key, value];
        }),
      ) as GridResult["gridData"];
    }

    // Limit the number of positions based on layout.screenData.gridLayout
    const { rows, columns } = layout.screenData.gridLayout;
    const validGridIds: GridId[] = [];
    for (let row = 1; row <= rows; row++) {
      for (let col = 1; col <= columns; col++) {
        validGridIds.push(`${row}-${col}` as GridId);
      }
    }
    // Filter gridData to only include valid grid IDs
    gridData = Object.fromEntries(
      Object.entries(gridData).filter(([key]) =>
        validGridIds.includes(key as GridId),
      ),
    ) as GridResult["gridData"];

    return {
      gridLayout: layout.screenData.gridLayout,
      gridData,
      nCams: layout.screenData.nCams,
      videoAspectRatio: layout.screenData.videoAspectRatio,
    };
  };

  const getPositionData: ScreenMethods["getPositionData"] = async (
    screenId,
    position,
  ) => {
    const layout = findScreenLayout(screenId);

    const gridId = positionToGridId(position);

    return gridToUse(layout)[gridId];
  };

  const clearPosition: ScreenMethods["clearPosition"] = (
    screenId,
    position,
  ) => {
    const layout = findScreenLayout(screenId);

    const gridId = positionToGridId(position);

    if (shouldUseCameraGrid(layout)) {
      layout.screenData.cameraGrid[gridId] = null;
    } else {
      layout.screenData.cageGrid[gridId] = null;
    }
    setScreenLayout(screenId, layout);
  };

  const setGridLayout: ScreenMethods["setGridLayout"] = (
    screenId,
    gridLayout,
  ) => {
    const layout = findScreenLayout(screenId);

    if (!isValidGridLayout(gridLayout)) {
      throw new Error(
        `Invalid grid layout: rows must be one of [1, 2, 3, 4] and columns must be one of [1, 2, 3, 4]`,
      );
    }

    // Handle edge case where focused cells might be on the wrong side after layout change
    const adjustedLayout = adjustFocusedPositionsForNewLayout(
      layout,
      gridLayout,
    );

    // Update the grid layout
    const updatedLayout = {
      ...adjustedLayout,
      screenData: {
        ...adjustedLayout.screenData,
        gridLayout,
      },
    };

    setScreenLayout(screenId, updatedLayout);
  };

  const setNCams: ScreenMethods["setNCams"] = (screenId, nCams) => {
    const layout = findScreenLayout(screenId);
    layout.screenData.nCams = nCams;
    setScreenLayout(screenId, layout);
  };

  const setVideoAspectRatio: ScreenMethods["setVideoAspectRatio"] = (
    screenId,
    videoAspectRatio,
  ) => {
    const layout = findScreenLayout(screenId);
    layout.screenData.videoAspectRatio = videoAspectRatio;
    setScreenLayout(screenId, layout);
  };

  const setPositionData: ScreenMethods["setPositionData"] = (
    screenId,
    targetPosition,
    positionData,
  ) => {
    const layout = findScreenLayout(screenId);
    const gridToUseForLayout = gridToUse(layout);
    const targetGridId = positionToGridId(targetPosition);

    if (!positionData) {
      gridToUseForLayout[targetGridId] = null;
    } else {
      let dataToSet: CellData;
      if (shouldUseCameraGrid(layout)) {
        dataToSet = validateCameraPositionData(positionData);
      } else {
        dataToSet = validateCagePositionData(positionData);
      }
      if (!dataToSet) {
        throw new Error(
          `Invalid position data for target position: ${targetGridId}`,
        );
      }

      const targetData = gridToUseForLayout[targetGridId];
      const targetOriginalPin = targetData?.pin;
      const targetIsFilled = targetData !== null;

      // Search for existing camera across all screens
      const existingPosition = findExistingCameraPosition(store, dataToSet);
      let existingScreenId: number | null = null;
      let existingGridId: GridId | null = null;

      if (existingPosition) {
        existingScreenId = existingPosition.screenId;
        existingGridId = existingPosition.gridId;
      }

      // Handle camera placement/swapping
      if (existingScreenId !== null && existingGridId !== null) {
        // Get the current data from the existing position to preserve camera order
        const existingLayout = findScreenLayout(existingScreenId);
        const existingGrid = gridToUse(existingLayout);
        const existingData = existingGrid[existingGridId];
        const existingOriginalPin = existingData?.pin;

        if (existingScreenId === screenId) {
          // Same screen swap logic
          if (targetIsFilled) {
            gridToUseForLayout[existingGridId] = targetData;
          } else {
            gridToUseForLayout[existingGridId] = null;
          }
          const existingCell = gridToUseForLayout[existingGridId];
          if (existingCell) {
            existingCell.pin = existingOriginalPin;
          }
        } else {
          // Cross-screen swap logic
          if (targetIsFilled) {
            existingGrid[existingGridId] = targetData;
          } else {
            existingGrid[existingGridId] = null;
          }
          const crossScreenCell = existingGrid[existingGridId];
          if (crossScreenCell) {
            crossScreenCell.pin = existingOriginalPin;
          }
          setScreenLayout(existingScreenId, existingLayout);
        }

        // Determine if we should preserve existing data or use newly validated data
        const sourceShouldUseCameraGrid = shouldUseCameraGrid(existingLayout);
        const targetShouldUseCameraGrid = shouldUseCameraGrid(layout);

        // Only preserve existing data if both source and target use the same grid type
        // This preserves camera order in cages when moving within cage mode
        // But uses validated data when moving between different modes (cam ↔ cage)
        const shouldPreserveExistingData =
          sourceShouldUseCameraGrid === targetShouldUseCameraGrid;

        if (shouldPreserveExistingData) {
          gridToUseForLayout[targetGridId] = existingData;
        } else {
          gridToUseForLayout[targetGridId] = dataToSet;
        }
      } else {
        // No existing cage found, use the newly validated data
        gridToUseForLayout[targetGridId] = dataToSet;
      }

      const targetCell = gridToUseForLayout[targetGridId];
      if (targetCell) {
        targetCell.pin = targetOriginalPin;
      }
    }

    setScreenLayout(screenId, layout);
  };

  const getPinData: ScreenMethods["getPinData"] = async (
    screenId,
    position,
  ) => {
    const layout = findScreenLayout(screenId);
    const gridId = positionToGridId(position);
    return gridToUse(layout)[gridId]?.pin ?? null;
  };

  const setPinData: ScreenMethods["setPinData"] = (
    screenId,
    position,
    pinData,
  ) => {
    const layout = findScreenLayout(screenId);
    const gridId = positionToGridId(position);

    if (pinData) {
      const existing =
        pinData.type === "feeding"
          ? findExistingPinPosition(
              store,
              pinData.feedlineId,
              pinData.machineId,
              screenId,
              gridId,
            )
          : null;
      if (existing) {
        const targetLayout =
          existing.screenId === screenId
            ? layout
            : findScreenLayout(existing.screenId);
        const existingCameraCell =
          targetLayout.screenData.cameraGrid[existing.gridId];
        if (existingCameraCell) {
          existingCameraCell.pin = undefined;
        }
        const existingCageCell =
          targetLayout.screenData.cageGrid[existing.gridId];
        if (existingCageCell) {
          existingCageCell.pin = undefined;
        }
        if (existing.screenId !== screenId) {
          setScreenLayout(existing.screenId, targetLayout);
        }
      }
    }

    if (layout.screenData.cameraGrid[gridId]) {
      layout.screenData.cameraGrid[gridId].pin = pinData ?? undefined;
    }
    if (layout.screenData.cageGrid[gridId]) {
      layout.screenData.cageGrid[gridId].pin = pinData ?? undefined;
    }

    setScreenLayout(screenId, layout);
  };

  const getAllPins: ScreenMethods["getAllPins"] = async () => {
    const screens = store.get("screenLayouts");
    const pins: Array<{ screenId: number; gridId: GridId; pin: PinData }> = [];
    for (const screenLayout of screens) {
      const grid = gridToUse(screenLayout);
      for (const [cellGridId, data] of Object.entries(grid)) {
        if (data?.pin) {
          pins.push({
            screenId: screenLayout.id,
            gridId: cellGridId as GridId,
            pin: data.pin,
          });
        }
      }
    }
    return pins;
  };

  const getCamerasOnPinnedCells: ScreenMethods["getCamerasOnPinnedCells"] =
    async () => {
      const screens = store.get("screenLayouts");
      const cameras: number[] = [];
      for (const screenLayout of screens) {
        const grid = gridToUse(screenLayout);
        for (const data of Object.values(grid)) {
          const displayed = data?.pin ? data.cameras[0] : undefined;
          if (displayed !== undefined) cameras.push(displayed);
        }
      }
      return cameras;
    };

  return {
    getGridData,
    getPositionData,
    clearPosition,
    setGridLayout,
    setNCams,
    setVideoAspectRatio,
    setPositionData,
    getPinData,
    setPinData,
    getAllPins,
    getCamerasOnPinnedCells,
  };
};
